/**
 * DailyReconcileBoxV2 — "Your day at a glance" coaching-frame widget.
 *
 * Columns: Activity | System saw | You logged | Offline activity.
 * When self > system the offline-activity column shows the positive delta
 * in neutral blue (assumes offline work that the system could not see).
 * When system > self the delta is shown in neutral gray. Neither is treated
 * as an anomaly in the rep view — copy stays coaching-first.
 *
 * The offline-activity note is required when any source for the tenant is
 * set to 'enforced' AND there is a positive delta. Otherwise the note is
 * optional. Tenant settings are read via useActivityCaptureSettings().
 *
 * Replaces DailyReconcileBox. Reads/writes via /api/me/reconcile/:date.
 */

import { useEffect, useMemo, useState } from "react";
import { tokens } from "@/components/operator/tokens";
import {
  getReconcile,
  putReconcile,
  ZERO_METRICS,
  type ReconcileMetrics,
  type ReconcileGetResponse,
} from "@/lib/reconcile-api";
import { useActivityCaptureSettings } from "@/hooks/useActivityCaptureSettings";

type MetricKey = keyof ReconcileMetrics;

interface RowDef {
  key: MetricKey;
  label: string;
  // when true, system-saw is intentionally blank (no auto source yet).
  selfOnly?: boolean;
}

const ROWS: RowDef[] = [
  { key: "outreach", label: "Calls" },
  { key: "voicemails_left", label: "Voicemails", selfOnly: true },
  { key: "live_conversations", label: "Conversations", selfOnly: true },
  { key: "emails_sent", label: "Emails sent" },
  { key: "linkedin_dms", label: "LinkedIn touches", selfOnly: true },
  { key: "first_meetings", label: "Meetings held" },
  { key: "qualified_handoffs", label: "Hunt items added", selfOnly: true },
];

// Neutral coaching frame: positive delta is treated as "offline activity"
// the system couldn't observe. No threshold-based amber flag.
function classifyDelta(self: number, auto: number, selfOnly: boolean) {
  if (selfOnly || auto === 0) return { delta: null as number | null, positive: false };
  const delta = self - auto;
  return { delta, positive: delta > 0 };
}

export interface DailyReconcileBoxV2Props {
  date?: string;
  onSaved?: () => void;
}

export default function DailyReconcileBoxV2({ date, onSaved }: DailyReconcileBoxV2Props) {
  const today = useMemo(() => date ?? new Date().toISOString().slice(0, 10), [date]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auto, setAuto] = useState<ReconcileMetrics>(ZERO_METRICS);
  const [self, setSelf] = useState<ReconcileMetrics>(ZERO_METRICS);
  const [locked, setLocked] = useState(false);
  const [offlineNote, setOfflineNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { settings: captureSettings } = useActivityCaptureSettings();

  // "Enforced mode" = any capture source is set to 'enforced' for this tenant.
  // When enforced, an offline-activity note is required whenever the rep has a
  // positive delta on any row.
  const enforcedMode = useMemo(() => {
    if (!captureSettings) return false;
    return Object.values(captureSettings).some((v) => v === "enforced");
  }, [captureSettings]);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    (async () => {
      try {
        const r: ReconcileGetResponse = await getReconcile(today);
        if (cancelled) return;
        setAuto({ ...ZERO_METRICS, ...r.auto });
        setSelf({ ...ZERO_METRICS, ...r.self });
        setLocked(r.locked);
        setOfflineNote(r.offline_note ?? "");
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message ?? "Failed to load reconciliation");
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [today]);

  const hasPositiveDelta = useMemo(() => {
    return ROWS.some((row) => {
      const { positive } = classifyDelta(
        Number(self[row.key] ?? 0),
        Number(auto[row.key] ?? 0),
        Boolean(row.selfOnly),
      );
      return positive;
    });
  }, [self, auto]);

  // Note required only when (a) tenant is in enforced mode and
  // (b) there is offline activity to explain.
  const needsNote = enforcedMode && hasPositiveDelta && offlineNote.trim().length === 0;

  async function handleSave() {
    if (locked) return;
    if (needsNote) return;
    setSaving(true);
    setError(null);
    try {
      const body: any = { ...self };
      if (offlineNote.trim().length > 0) body.offline_note = offlineNote.trim();
      const res = await putReconcile(today, body);
      setLocked(res.locked);
      onSaved?.();
    } catch (e: any) {
      setError(e?.message ?? "Save failed");
    } finally {
      setSaving(false);
    }
  }

  function updateSelf(key: MetricKey, value: number) {
    setSelf((prev) => ({ ...prev, [key]: Number.isFinite(value) ? value : 0 }));
  }

  return (
    <section
      style={{
        background: tokens.color.canvas,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.card,
        padding: 18,
        boxShadow: tokens.shadow.card,
        fontFamily: tokens.font.body,
      }}
      data-testid="daily-reconcile-v2"
    >
      <header style={{ marginBottom: 14 }}>
        <div
          style={{
            fontFamily: tokens.font.display,
            fontWeight: 700,
            fontSize: 16,
            color: tokens.color.textPrimary,
          }}
        >
          Your day at a glance
        </div>
        <div style={{ fontSize: 12, color: tokens.color.textMuted, marginTop: 4 }}>
          {today} · what the system saw vs what you logged. Add anything we missed.
        </div>
      </header>

      {!loaded && (
        <div style={{ fontSize: 13, color: tokens.color.textMuted }}>Loading…</div>
      )}

      {loaded && (
        <>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ background: tokens.color.surfaceMute }}>
                  <Th align="left">Activity</Th>
                  <Th align="right">System saw</Th>
                  <Th align="right">You logged</Th>
                  <Th align="right">Offline activity</Th>
                  <Th align="left"> </Th>
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => {
                  const selfVal = Number(self[row.key] ?? 0);
                  const autoVal = Number(auto[row.key] ?? 0);
                  const { delta, positive } = classifyDelta(
                    selfVal,
                    autoVal,
                    Boolean(row.selfOnly),
                  );
                  return (
                    <tr
                      key={row.key}
                      style={{
                        borderTop: `1px solid ${tokens.color.borderSoft}`,
                      }}
                    >
                      <Td>
                        <span style={{ fontWeight: 600 }}>{row.label}</span>
                      </Td>
                      <Td align="right">
                        {row.selfOnly ? (
                          <span style={{ color: tokens.color.textSubtle }}>—</span>
                        ) : (
                          autoVal
                        )}
                      </Td>
                      <Td align="right">
                        <input
                          type="number"
                          min={0}
                          disabled={locked}
                          value={selfVal}
                          onChange={(e) =>
                            updateSelf(row.key, Number(e.target.value || 0))
                          }
                          style={{
                            width: 72,
                            padding: "4px 8px",
                            borderRadius: 6,
                            border: `1px solid ${tokens.color.border}`,
                            textAlign: "right",
                            background: locked
                              ? tokens.color.surfaceMute
                              : tokens.color.canvas,
                            fontFamily: tokens.font.body,
                            color: tokens.color.textPrimary,
                          }}
                          data-testid={`reconcile-v2-${row.key}`}
                        />
                      </Td>
                      <Td align="right">
                        {delta === null || delta === 0 ? (
                          <span style={{ color: tokens.color.textSubtle }}>—</span>
                        ) : positive ? (
                          <span
                            style={{
                              fontWeight: 700,
                              color: "#1F2937",
                            }}
                            title="Looks like offline work we couldn't see — add a quick note so it counts."
                          >
                            +{delta}
                          </span>
                        ) : (
                          <span
                            style={{
                              fontWeight: 600,
                              color: tokens.color.textMuted,
                            }}
                            title="We saw more than you logged — your daily total has been topped up."
                          >
                            {delta}
                          </span>
                        )}
                      </Td>
                      <Td>
                        {row.selfOnly ? (
                          <span style={{ color: tokens.color.textSubtle, fontSize: 12 }}>
                            self only
                          </span>
                        ) : null}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(hasPositiveDelta || offlineNote.length > 0) && (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 6,
                background: "#EFF6FF",
                border: "1px solid #BFDBFE",
              }}
            >
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: tokens.color.textPrimary,
                  marginBottom: 6,
                }}
              >
                Offline activity
                {needsNote ? (
                  <span style={{ color: "#1F2937", marginLeft: 6 }}>required</span>
                ) : (
                  <span style={{ color: tokens.color.textMuted, marginLeft: 6 }}>optional</span>
                )}
              </label>
              <textarea
                value={offlineNote}
                disabled={locked}
                onChange={(e) => setOfflineNote(e.target.value)}
                placeholder="Door-knocks, hallway chats, calls from your cell — anything we couldn't see."
                rows={2}
                style={{
                  width: "100%",
                  padding: "8px 10px",
                  borderRadius: 6,
                  border: `1px solid ${tokens.color.border}`,
                  fontFamily: tokens.font.body,
                  fontSize: 13,
                  resize: "vertical",
                }}
                data-testid="reconcile-v2-offline-note"
              />
            </div>
          )}

          {error && (
            <div
              style={{
                marginTop: 12,
                padding: 8,
                fontSize: 12,
                color: tokens.color.danger,
                background: tokens.color.dangerBg,
                borderRadius: 6,
              }}
            >
              {error}
            </div>
          )}

          <footer
            style={{
              marginTop: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span style={{ fontSize: 12, color: tokens.color.textMuted }}>
              {locked
                ? "Today is locked in. Ask a manager if you need to edit."
                : needsNote
                ? "Add an offline-activity note to enable save."
                : ""}
            </span>
            <button
              type="button"
              onClick={handleSave}
              disabled={locked || saving || needsNote}
              style={{
                background: locked || needsNote ? tokens.color.textSubtle : tokens.color.red,
                color: tokens.color.softWhite,
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                fontWeight: 600,
                fontSize: 13,
                cursor: locked || needsNote || saving ? "not-allowed" : "pointer",
                fontFamily: tokens.font.body,
              }}
              data-testid="reconcile-v2-save"
            >
              {locked
                ? "Locked in"
                : saving
                ? "Saving…"
                : "Save my day"}
            </button>
          </footer>
        </>
      )}
    </section>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      style={{
        padding: "10px 12px",
        textAlign: align,
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: tokens.color.textMuted,
        fontWeight: 700,
        borderBottom: `1px solid ${tokens.color.border}`,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <td
      style={{
        padding: "10px 12px",
        textAlign: align,
        verticalAlign: "middle",
        color: tokens.color.textPrimary,
      }}
    >
      {children}
    </td>
  );
}
